// =============================================================================
// VITE ENTRY POINT - Main bootstrapping file for Indexed.IO
// =============================================================================
// This file replaces Grunt's script concatenation and manual HTML <script> tags

// ===== EXTERNAL DEPENDENCIES (in dependency order) =====

// MUST be the first import: sets window.jQuery before Angular evaluates.
// Angular's bindJQuery() fires at module-evaluation time (not at bootstrap
// time), so window.jQuery must be set as a side-effect of a module that is
// imported before the angular import chain. Moving this to main.js body
// would be too late — the body only runs after all imports have evaluated.
import './scripts/setup-globals';

// Core dependencies
import 'uuidjs';
import 'jquery';
import 'jquery-ui/dist/jquery-ui';
import './vendor/intlTelInput-jquery.js';
import moment from 'moment';
import 'moment-timezone';
// Expose moment as a browser global so UMD libraries (e.g. datetimepicker) can find it
window.moment = moment;

// Angular Core & Official Modules
import 'angular';
import 'angular-route';
import 'angular-resource';
import 'angular-cookies';
import 'angular-animate';

// Angular Third-party Modules
import 'angular-ui-bootstrap/dist/ui-bootstrap-tpls';
import 'angular-ui-sortable';
import 'angular-ui-utils/modules/mask/mask.js';
import 'angular-hotkeys';
import 'underscore';
import he from 'he';
// Expose he as a browser global so grid renderers can find it
window.he = he;
import 'angular-bootstrap-colorpicker';
import 'bootstrap-slider';
import './vendor/bootstrap-datetimepicker.js';

// Charting & Data Libraries
import * as d3 from 'd3';
// Expose d3 as browser global so chart directives (timeline.ts, chord.ts) can find it
window.d3 = d3;
import c3 from 'c3';
// Expose c3 as browser global so UMD-dependent directives (e.g. pie.ts, gauge.ts) can find it
window.c3 = c3;
import 'jsonformatter';

// Rich Text Editor
import './scripts/vendor/angular-compat-shim.js'; // restore angular.lowercase/uppercase removed in Angular 1.7
import 'textangular/dist/textAngular-sanitize.min.js'; // textAngular's own $sanitize (sets version='taSanitize')
import 'textangular';

// Video Player
import 'videogular';
import 'videogular-controls';
import 'videogular-overlay-play';
import 'videogular-poster';
import 'videogular-buffering';

// Data Grids & Tables
import './scripts/ag-grid-init.ts';
import 'angular-smart-table';
import 'angular-filter';
import 'checklist-model';
import 'clipboard';
import 'ngclipboard';

// Magicsuggest autocomplete
import 'magicsuggest';

// Additional Angular modules
import 'intro.js';
import 'ng-stomp';
import 'ng-intl-tel-input/ng-intl-tel-input.module.js';
import 'ng-intl-tel-input/ng-intl-tel-input.provider.js';
import 'ng-intl-tel-input/ng-intl-tel-input.directive.js';
import '@stripe/stripe-js';

// Custom vendor scripts (must come after external libs they depend on)
import './scripts/vendor/pusher.color.js';
import './scripts/vendor/controls/angular-toggle-switch.min.js';
import './scripts/vendor/l10n.js';
import './scripts/vendor/pdfviewer/custom/pdf.js';
import './scripts/vendor/pdfviewer/compatibility.js';
import './scripts/vendor/pdfviewer/custom/viewer.js';
import './scripts/vendor/pdfviewer/custom/redact.js';
import './scripts/plugins/textAngularPlugin.js';
import './scripts/vendor/ng-stomp.standalone.min.js';

// ===== APPLICATION CODE (TypeScript compiled output) =====

// Core utilities & models
import './scripts/Utilities.ts';

// Base model interfaces (loaded early to establish namespaces)
import './scripts/model/monitoring/IMonitorable.ts';

// Angular app module (declares frontendApp)
import './scripts/app-module.ts';

// All services (coordinated imports with proper dependency order)
import './scripts/services/index.ts';

// App configuration and routes
import './scripts/frontend.ts';

// HTTP Interceptors
import './scripts/providers/httpinterceptor.js';
import './scripts/providers/auth-interceptor.ts';
import './scripts/providers/ajax-dedup.ts';

// Models (in load order)
import './scripts/model/resources/ResourceEntityCategory.ts';
import './scripts/model/resources/ResourceEntityType.ts';
import './scripts/model/SortDirection.ts';
import './scripts/model/user/Preferences.ts';
import './scripts/model/SortOperation.ts';
import './scripts/model/controls/DropdownOption.ts';
import './scripts/model/controls/ListItem.ts';
import './scripts/model/controls/grid/GroupedType.ts';
import './scripts/model/controls/grid/IGroupedItem.ts';
import './scripts/model/job/IJob.ts';
import './scripts/model/job/Job.ts';
import './scripts/model/job/CollectionJob.ts';
import './scripts/model/job/TiffBatchJob.ts';
import './scripts/model/project/Status.ts';
import './scripts/model/project/Type.ts';
import './scripts/model/project/UsageType.ts';
import './scripts/model/tagging/Tag.ts';
import './scripts/model/collections/ICollection.ts';
import './scripts/model/collections/Collection.ts';
import './scripts/model/exporting/EndorsementType.ts';
import './scripts/model/exporting/Endorsement.ts';
import './scripts/model/exporting/Export.ts';
import './scripts/model/tiffing/TiffBatch.ts';
import './scripts/model/tagging/TagForm.ts';
import './scripts/model/metrics/ProcessingMetrics.ts';
import './scripts/model/search/advanced/SearchVisibility.ts';
import './scripts/model/search/advanced/Field.ts';
import './scripts/model/search/advanced/DateRangeField.ts';
import './scripts/model/search/advanced/NumberRangeField.ts';
import './scripts/model/search/advanced/MultiFullTextField.ts';
import './scripts/model/search/advanced/MultiTextField.ts';
import './scripts/model/search/advanced/MultiTagField.ts';
import './scripts/model/search/advanced/MultiNamedEntityField.ts';
import './scripts/model/search/advanced/MultiSearchTermField.ts';
import './scripts/model/search/advanced/SingleFullTextField.ts';
import './scripts/model/search/advanced/MultiCollectionField.ts';
import './scripts/model/search/advanced/SingleTextField.ts';
import './scripts/model/search/advanced/SingleBooleanField.ts';
import './scripts/model/search/advanced/BatesRangeField.ts';
import './scripts/model/search/advanced/SearchExpression.ts';
import './scripts/model/search/ISearch.ts';
import './scripts/model/search/BaseSearch.ts';
import './scripts/model/search/basic/BasicSearch.ts';
import './scripts/model/search/advanced/AdvancedSearch.ts';
import './scripts/model/batching/Batch.ts';
import './scripts/model/authentication/Role.ts';
import './scripts/model/search/advanced/ComparisonType.ts';
import './scripts/model/tagging/TagFormElement.ts';
import './scripts/model/tagging/TagFormElementTag.ts';
import './scripts/model/tagging/TagFormElementText.ts';
import './scripts/model/terms/SearchTermList.ts';
import './scripts/model/terms/SearchTermListGroupedItem.ts';
import './scripts/model/terms/SearchTerm.ts';
import './scripts/model/terms/SearchTermGroupedItem.ts';
import './scripts/model/terms/SearchTermsPage.ts';
import './scripts/model/exporting/RelativityFieldMappings.ts';
import './scripts/model/exporting/RelativitySettings.ts';

// Services are now imported via services/index.ts for proper dependency coordination

// Directives
import './scripts/directives/factory.ts';
import './scripts/directives/auditing/audit.ts';
import './scripts/directives/charting/valueType.ts';
import './scripts/vendor/charting/custom/liquidFillGauge.js';
import './scripts/directives/controls/thumbnail.ts';
import './scripts/directives/controls/dateTimePicker.ts';
import './scripts/directives/controls/comboBox.ts';
import './scripts/directives/controls/childWindow.ts';
import './scripts/directives/search/basic/search.ts';
import './scripts/directives/search/advanced/selector.ts';
import './scripts/directives/search/advanced/expressionGroup.ts';
import './scripts/directives/search/advanced/expressionBuilder.ts';
import './scripts/directives/search/advanced/search.ts';
import './scripts/directives/search/advanced/shareSearch.ts';
import './scripts/directives/controls/buttonDropdown.ts';
import './scripts/directives/controls/grid/renderers/jsonCellRenderer.ts';
import './scripts/directives/controls/grid/renderers/resourceCellRenderer.ts';
import './scripts/directives/controls/grid/renderers/conversationCellRenderer.ts';
import './scripts/directives/controls/grid/renderers/messageCellRenderer.ts';
import './scripts/directives/controls/grid/dataGrid.ts';
import './scripts/directives/utilities/equal.js';
import './scripts/directives/utilities/bindHtml.js';
import './scripts/directives/authentication/Login.ts';
import './scripts/directives/controls/star.js';
import './scripts/directives/authentication/role.js';
import './scripts/directives/controls/gravatar.ts';
import './scripts/directives/controls/badge.ts';
import './scripts/directives/controls/toggle.js';
import './scripts/directives/notification/notifications.js';
import './scripts/directives/notification/notification.js';
import './scripts/directives/charting/area.js';
import './scripts/directives/charting/column.js';
import './scripts/directives/controls/droptarget.js';
import './scripts/directives/notification/inputNotice.js';
import './scripts/directives/utilities/filter.js';
import './scripts/directives/controls/navigation.js';
import './scripts/directives/controls/href.js';
import './scripts/directives/tagging/tag.js';
import './scripts/directives/tagging/tagPopoverPopup.js';
import './scripts/directives/tagging/tagPopover.js';
import './scripts/directives/tagging/addTag.ts';
import './scripts/directives/tagging/tagBoolean.js';
import './scripts/directives/tagging/tagBooleanItem.js';
import './scripts/directives/tagging/TagFormElement.ts';
import './scripts/directives/theming/charting/theme.ts';
import './scripts/directives/theming/theme.js';
import './scripts/directives/theming/theme_image.js';
import './scripts/directives/theming/theme_powered_by.js';
import './scripts/directives/resource/resourceFilter.ts';
import './scripts/directives/utilities/scrolling.ts';
import './scripts/directives/controls/popoverPopup.js';
import './scripts/directives/controls/popover.js';
import './scripts/directives/utilities/charCode.js';
import './scripts/directives/tracking/anchorTracker.ts';
import './scripts/directives/tracking/buttonTracker.ts';
import './scripts/directives/charting/row.ts';
import './scripts/directives/charting/timeline.ts';
import './scripts/directives/authentication/roleEditor.ts';
import './scripts/directives/authentication/showIf.ts';
import './scripts/directives/controls/editableField.ts';
import './scripts/directives/controls/progress.ts';
import './scripts/directives/review/advancedViewer.ts';
import './scripts/directives/review/basicViewer.ts';
import './scripts/directives/charting/chord.ts';
import './scripts/directives/charting/pie.ts';
import './scripts/directives/charting/gauge.ts';
import './scripts/directives/charting/progressMeter.ts';
import './scripts/directives/charting/bar.ts';
import './scripts/directives/charting/burnDown.ts';
import './scripts/directives/reporting/controls/dataOverview.ts';
import './scripts/directives/controls/slider.ts';
import './scripts/directives/controls/tooltip.ts';
import './scripts/directives/controls/jsonViewer.ts';
import './scripts/directives/review/reviewStats.ts';
import './scripts/directives/review/batchCount.ts';
import './scripts/directives/controls/splitter.ts';
import './scripts/directives/exporting/exportInfo.ts';
import './scripts/directives/chatting/chat.ts';

// Filters
import './scripts/filters/bytes.ts';
import './scripts/filters/titlecase.ts';
import './scripts/filters/chatparticipants.ts';
import './scripts/filters/truncate.js';
import './scripts/filters/pad.js';
import './scripts/filters/ucwords.js';
import './scripts/filters/newlines.js';

// Controllers (must come after services and directives)
import './scripts/controllers/Factory.ts';
import './scripts/controllers/HomeController.ts';
import './scripts/controllers/authentication/ResetPasswordController.ts';
import './scripts/controllers/settings.js';
import './scripts/controllers/collections/CollectionController.ts';
import './scripts/controllers/review/BrowseController.ts';
import './scripts/controllers/resource/ResourceController.ts';
import './scripts/controllers/organization.js';
import './scripts/controllers/upload.js';
import './scripts/controllers/import.js';
import './scripts/controllers/search/SearchController.ts';
import './scripts/controllers/search/SaveSearchModalController.ts';
import './scripts/controllers/load_file.js';
import './scripts/controllers/notification.js';
import './scripts/controllers/tagging/TagFormController.ts';
import './scripts/controllers/trial_warning.js';
import './scripts/controllers/reset_password_button.js';
import './scripts/controllers/add_account_modal.js';
import './scripts/controllers/reset_password_modal.js';
import './scripts/controllers/collections/AddCollectionController.ts';
import './scripts/controllers/exporting/ExportController.ts';
import './scripts/controllers/exporting/ExportInfoController.ts';
import './scripts/controllers/add_custodian_modal.js';
import './scripts/controllers/add_tag_modal.js';
import './scripts/controllers/reporting/add_report_modal.js';
import './scripts/controllers/reporting/add_tag_report_modal.js';
import './scripts/controllers/add_user_modal.js';
import './scripts/controllers/request_reviewer_modal.js';
import './scripts/controllers/confirm_modal.js';
import './scripts/controllers/browser_modal.js';
import './scripts/controllers/layer.js';
import './scripts/controllers/batching/AddBatchingModalController.ts';
import './scripts/controllers/batching/AssignBatchModalController.ts';
import './scripts/controllers/mfa/MFAModalController.ts';
import './scripts/controllers/project/ProjectController.ts';
import './scripts/controllers/project/ProjectDetailController.ts';
import './scripts/controllers/project/ProjectAdminController.ts';
import './scripts/controllers/project/GetStartedController.ts';
import './scripts/controllers/project/AddProjectController.ts';
import './scripts/controllers/project/CreditCardModalController.ts';
import './scripts/controllers/project/HibernateModalController.ts';
import './scripts/controllers/project/ReActivateModalController.ts';
import './scripts/controllers/project/ProjectDeleteModalController.ts';
import './scripts/controllers/review/ReplacePdfModalController.ts';
import './scripts/controllers/terms/AddSearchTermListModalController.ts';
import './scripts/controllers/terms/SearchTermController.ts';
import './scripts/controllers/404.js';

// Chat widget show/hide helpers (Teams widget injection, gated by subdomain)
import './scripts/chat-widget.js';

// ===== STYLES =====
// Third-party CSS (resolved by Vite from node_modules)
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/css/bootstrap-theme.css';
import '@fortawesome/fontawesome-free/css/all.css';
import 'angular-ui-bootstrap/dist/ui-bootstrap-csp.css';
import 'angular-hotkeys/build/hotkeys.css';
import './vendor/intlTelInput.css';
import 'jquery-ui/themes/base/all.css';
import 'jquery-ui/themes/base/theme.css';
import './vendor/bootstrap-datetimepicker.css';
import 'bootstrap-slider/dist/css/bootstrap-slider.css';
import 'c3/c3.css';
import 'textangular/dist/textAngular.css';
import 'angular-bootstrap-colorpicker/css/colorpicker.css';
import 'intro.js/introjs.css';
import 'jsonformatter/dist/json-formatter.css';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-bootstrap.css';
// App SCSS (variables, mixins, ag-grid, partials)
import './styles/main.scss';

// ===== ANGULAR BOOTSTRAP =====
// Manual bootstrap after all modules are registered — must be here at the
// bottom of main.js (not in index.html as ng-app) because type="module"
// scripts execute asynchronously. Angular's auto-bootstrap fires on
// DOMContentLoaded before ES modules have evaluated, so frontendApp would
// not yet be registered. By bootstrapping here we guarantee all imports
// above have completed before angular.bootstrap() is called.
document.addEventListener('DOMContentLoaded', () => {
  const env = import.meta.env.VITE_ENVIRONMENT || 'prod';
  fetch('https://iio-assets.s3.amazonaws.com/features/features.json')
    .then(r => r.json())
    .then(data => {
      window.__features = data[env] || {};
    })
    .catch(() => {
      window.__features = {};
    })
    .finally(() => {
      window.dispatchEvent(new CustomEvent('featuresLoaded', { detail: window.__features }));
    });

  const appElement = document.getElementById('ng-app') || document.documentElement;
  angular.bootstrap(appElement, ['frontendApp']);
});
